import React, { useState, useRef, useEffect } from "react";
import InnoIcons from "@InnoIcons/";
import SC from "./style";
import { InputField } from "@components/";
import theme from "@theme/";

const OccupantAreaFilterDropdown = ({
  label = "Occupant Area",
  selectedHeaderId,
  onHeaderChange,
  selectedValue = "",
  headerOptionsValueName = "Occupant Area",
  onValueChange,
  buttonWidth = "200px",
  minWidth = "250px",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [unit, setUnit] = useState("m2");
  const [inputValue, setInputValue] = useState(selectedValue);
  const dropdownRef = useRef();

  // Operator options
  const headerOptions = [
    { id: "equals", label: `${headerOptionsValueName} = (equals)` },
    { id: "lessThanEqual", label: `${headerOptionsValueName} <= (less than)` },
    {
      id: "greaterThanEqual",
      label: `${headerOptionsValueName} >= (greater than)`,
    },
  ];

  const selectedHeader = headerOptions.find((h) => h.id === selectedHeaderId);

  // Convert input to m² before sending to backend
  const convertToM2 = (value, unit) => {
    const num = parseFloat(value);
    if (isNaN(num) || num <= 0) return 0;
    switch (unit) {
      case "ft2":
        return +(num * 0.092903).toFixed(2); // ft² → m²
      case "m2":
      default:
        return num; // already m²
    }
  };

  const handleUpdate = () => {
    let valueInM2 = 0;

    if (inputValue && !isNaN(inputValue)) {
      const num = parseFloat(inputValue);
      if (unit === "ft2") {
        // Convert ft² → m²
        valueInM2 = +(num * 0.092903).toFixed(2); // keeps 2 decimal points
      } else {
        valueInM2 = num; // already m²
      }
    }
    console.log("Input Value:", inputValue, unit);

    console.log("Sending to backend (in m²):", valueInM2);

    if (!valueInM2 || valueInM2 <= 0) {
      onValueChange?.(null); // reset if invalid
    } else {
      onValueChange?.(valueInM2); // always send m²
    }

    setIsOpen(false);
    setIsHeaderExpanded(false);
  };

  const handleUnitChange = (e) => setUnit(e.target.value);

  const handleInputChange = (name, val) => {
    if (/^\d*\.?\d*$/.test(val)) setInputValue(val);
  };

  const handleHeaderClick = (headerId) => {
    onHeaderChange(headerId);
    setIsHeaderExpanded(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setIsHeaderExpanded(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Display value in chosen unit
  const displayValue = unit === "ft2" ? inputValue || "" : inputValue || "";

  return (
    <SC.DropdownContainer ref={dropdownRef}>
      <SC.DropdownButton
        minWidth={buttonWidth}
        isOpen={isOpen}
        isActive={!!selectedValue && Number(selectedValue) > 0}
        onClick={() => setIsOpen((p) => !p)}
      >
        {selectedValue && Number(selectedValue) > 0
          ? `${label}: ${
              selectedHeader?.id === "equals"
                ? "="
                : selectedHeader?.id === "lessThanEqual"
                ? "<="
                : selectedHeader?.id === "greaterThanEqual"
                ? ">="
                : ""
            } ${displayValue} ${unit === "m2" ? "m²" : "ft²"}`
          : `${label}`}
        <SC.ChevronIcon
          size={14}
          isOpen={isOpen}
          isActive={!!selectedValue && Number(selectedValue) > 0}
        />
      </SC.DropdownButton>

      {isOpen && (
        <SC.DropdownMenu isOpen={isOpen} minWidth={minWidth}>
          {/* Header/Operator */}
          <SC.DropdownHeader
            isClickable
            onClick={() => setIsHeaderExpanded((p) => !p)}
          >
            <SC.HeaderText>
              {selectedHeader?.label || "Select Condition"}
            </SC.HeaderText>
            <SC.HeaderChevron isExpanded={isHeaderExpanded} />
          </SC.DropdownHeader>

          <SC.HeaderOptionsContainer isVisible={isHeaderExpanded}>
            {headerOptions.map((header) => (
              <SC.HeaderOption
                key={header.id}
                isSelected={header.id === selectedHeaderId}
                onClick={() => handleHeaderClick(header.id)}
              >
                {header.label}
              </SC.HeaderOption>
            ))}
          </SC.HeaderOptionsContainer>

          {/* Input + Unit */}
          <div
            style={{ display: "flex", alignItems: "center", margin: "30px 0" }}
          >
            <InputField
              name="area"
              type="number"
              placeholder="Enter size"
              value={inputValue}
              onChange={handleInputChange}
              style={{ width: "45%", height: "34px", borderRadius: "5px" }}
            />
            <SC.UnitSelect value={unit} onChange={handleUnitChange}>
              <option value="m2">m²</option>
              <option value="ft2">ft²</option>
            </SC.UnitSelect>
          </div>

          {/* Footer */}
          <SC.DropdownFooter
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <SC.UpdateButton
              onClick={handleUpdate}
              style={{
                background: theme.gradient.themeGradient,
                color: "#fff",
              }}
            >
              Update
            </SC.UpdateButton>

            <SC.ClearAllButton
              onClick={() => {
                setInputValue(""); // clear input
                onValueChange?.(null); // reset backend filter
                setIsOpen(false);
                setIsHeaderExpanded(false);
              }}
            >
              Clear
            </SC.ClearAllButton>
          </SC.DropdownFooter>
        </SC.DropdownMenu>
      )}
    </SC.DropdownContainer>
  );
};

export default OccupantAreaFilterDropdown;
